// frontend/src/pages/SettingsPage.tsx - UPDATED FOR HOOKS/STORE INTEGRATION
// MODIFIED FUNCTION: Updated to use new API hooks, store, and utils following the pattern of CorrespondencesPage, ResponsesPage, and ProjectsPage

import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, debounce } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';

// UPDATED: Use hooks following new architecture
import { 
  useNotificationSettings,
  useUpdateNotificationSettings 
} from '@/hooks/useNotifications';

import { 
  useDashboardPreferences,
  useUpdateDashboardPreferences 
} from '@/hooks/useDashboard';

// UPDATED: Use API types instead of local interfaces
import type {
  UserRole,
  NotificationType,
  Priority
} from '@/api/types';

// ===== LOCAL INTERFACES =====

// Keep local interfaces for settings that don't have API types yet
interface NotificationPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  inAppNotifications: boolean;
  notificationTypes: {
    CORRESPONDENCE_CREATED: boolean;
    RESPONSE_ASSIGNED: boolean;
    RESPONSE_APPROVED: boolean;
    RESPONSE_REJECTED: boolean;
    WORKFLOW_STAGE_CHANGED: boolean;
    GROUP_MEMBERSHIP_CHANGED: boolean;
    PROJECT_ASSIGNED: boolean;
    DOCUMENT_UPLOADED: boolean;
    SYSTEM_ALERT: boolean;
  };
  quietHours?: {
    enabled: boolean;
    startTime: string;
    endTime: string;
    timezone: string;
  };
  digestFrequency: 'none' | 'daily' | 'weekly';
}

interface DashboardPreferences {
  dashboardRefreshInterval: number;
  defaultView: 'grid' | 'list' | 'cards';
  showCompletedActions: boolean;
  actionFilters: boolean;
  theme: 'light' | 'dark' | 'auto';
  autoRefresh: boolean;
  widgetConfiguration?: {
    showMetrics: boolean;
    showActivity: boolean;
    showTasks: boolean;
    showNotifications: boolean;
    showStorage: boolean;
  };
  compactMode: boolean;
  timeFormat: '12h' | '24h';
  dateFormat: string;
}

interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  language: string;
  timezone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
}

// ===== UTILITY COMPONENTS =====

const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
}> = memo(({ checked, onChange, disabled = false, id }) => {
  return (
    <label htmlFor={id} className="relative inline-flex items-center cursor-pointer">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="sr-only peer"
        aria-describedby={id ? `${id}-description` : undefined}
      />
      <div className={cn(
        "w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600",
        disabled && "opacity-50 cursor-not-allowed"
      )}></div>
    </label>
  );
});

const TimeInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
}> = memo(({ value, onChange, disabled = false, id }) => {
  return (
    <input
      id={id}
      type="time"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  );
});

// ===== ERROR BOUNDARY =====

class SettingsErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('SettingsPage Error Boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-6 text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Settings Error</h2>
          <p className="text-gray-600 mb-4">Something went wrong loading the settings page.</p>
          <Button onClick={() => this.setState({ hasError: false })}>
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== MAIN COMPONENT =====

export const SettingsPage: React.FC = () => {
  // ===== HOOKS =====
  
  const { user } = useAuthStore();
  const { setGlobalError, clearGlobalError, setTheme } = useAppStore();

  // UPDATED: Use new hooks following the pattern
  const {
    data: notificationSettingsData,
    loading: notificationLoading,
    error: notificationError,
    refetch: refetchNotifications
  } = useNotificationSettings();

  const {
    data: dashboardPreferencesData,
    loading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard
  } = useDashboardPreferences();

  const {
    mutate: updateNotificationSettings,
    loading: updateNotificationLoading,
    error: updateNotificationError
  } = useUpdateNotificationSettings({
    onSuccess: () => {
      setSuccessMessage('Notification preferences updated successfully');
      refetchNotifications();
      setHasChanges(false);
    },
    onError: (error) => {
      setGlobalError(error.message || 'Failed to update notification preferences');
    }
  });

  const {
    mutate: updateDashboardPreferences,
    loading: updateDashboardLoading,
    error: updateDashboardError
  } = useUpdateDashboardPreferences({
    onSuccess: () => {
      setSuccessMessage('Dashboard preferences updated successfully');
      refetchDashboard();
      setHasChanges(false);
    },
    onError: (error) => {
      setGlobalError(error.message || 'Failed to update dashboard preferences');
    }
  });

  // ===== LOCAL STATE =====

  const [notificationSettings, setNotificationSettings] = useState<NotificationPreferences>({
    emailNotifications: true,
    pushNotifications: false,
    inAppNotifications: true,
    notificationTypes: {
      CORRESPONDENCE_CREATED: true,
      RESPONSE_ASSIGNED: true,
      RESPONSE_APPROVED: true,
      RESPONSE_REJECTED: true,
      WORKFLOW_STAGE_CHANGED: true,
      GROUP_MEMBERSHIP_CHANGED: false,
      PROJECT_ASSIGNED: true,
      DOCUMENT_UPLOADED: false,
      SYSTEM_ALERT: true,
    },
    quietHours: {
      enabled: false,
      startTime: '22:00',
      endTime: '08:00',
      timezone: 'UTC'
    },
    digestFrequency: 'daily'
  });

  const [dashboardSettings, setDashboardSettings] = useState<DashboardPreferences>({
    dashboardRefreshInterval: 300,
    defaultView: 'grid',
    showCompletedActions: false,
    actionFilters: true,
    theme: 'light',
    autoRefresh: true,
    widgetConfiguration: {
      showMetrics: true,
      showActivity: true,
      showTasks: true,
      showNotifications: true,
      showStorage: true
    },
    compactMode: false,
    timeFormat: '24h',
    dateFormat: 'yyyy-MM-dd'
  });

  const [userSettings, setUserSettings] = useState<UserPreferences>({
    theme: 'system',
    language: 'en',
    timezone: 'UTC',
    dateFormat: 'yyyy-MM-dd',
    timeFormat: '24h'
  });

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<'notifications' | 'dashboard' | 'display' | 'account'>('notifications');

  // ===== EFFECTS =====

  // Update local state when data loads
  useEffect(() => {
    if (notificationSettingsData) {
      setNotificationSettings(prev => ({
        ...prev,
        ...notificationSettingsData
      }));
    }
  }, [notificationSettingsData]);

  useEffect(() => {
    if (dashboardPreferencesData) {
      setDashboardSettings(prev => ({
        ...prev,
        ...dashboardPreferencesData
      }));
    }
  }, [dashboardPreferencesData]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Clear errors when component mounts
  useEffect(() => {
    return () => clearGlobalError();
  }, [clearGlobalError]);

  // ===== HANDLERS =====

  const handleNotificationChange = useCallback((field: keyof NotificationPreferences, value: any) => {
    setNotificationSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  }, []);

  const handleDashboardChange = useCallback((field: keyof DashboardPreferences, value: any) => {
    setDashboardSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  }, []);

  const handleUserChange = useCallback((field: keyof UserPreferences, value: any) => {
    setUserSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
    
    // Apply theme changes immediately
    if (field === 'theme') {
      setTheme(value);
    }
  }, [setTheme]);

  const handleQuietHoursChange = useCallback((field: keyof NonNullable<NotificationPreferences['quietHours']>, value: any) => {
    setNotificationSettings(prev => ({
      ...prev,
      quietHours: {
        ...prev.quietHours!,
        [field]: value
      }
    }));
    setHasChanges(true);
  }, []);

  const handleNotificationTypesChange = useCallback((type: keyof NotificationPreferences['notificationTypes'], enabled: boolean) => {
    setNotificationSettings(prev => ({
      ...prev,
      notificationTypes: {
        ...prev.notificationTypes,
        [type]: enabled
      }
    }));
    setHasChanges(true);
  }, []);

  const handleWidgetConfigChange = useCallback((widget: keyof NonNullable<DashboardPreferences['widgetConfiguration']>, enabled: boolean) => {
    setDashboardSettings(prev => ({
      ...prev,
      widgetConfiguration: {
        ...prev.widgetConfiguration!,
        [widget]: enabled
      }
    }));
    setHasChanges(true);
  }, []);

  // Debounced save functions
  const debouncedSaveNotifications = useMemo(
    () => debounce(() => {
      const apiPayload = {
        ...notificationSettings,
        digestFrequency: notificationSettings.digestFrequency as 'none' | 'daily' | 'weekly'
      };
      updateNotificationSettings(apiPayload);
    }, 1000),
    [updateNotificationSettings, notificationSettings]
  );

  const debouncedSaveDashboard = useMemo(
    () => debounce(() => {
      updateDashboardPreferences(dashboardSettings);
    }, 1000),
    [updateDashboardPreferences, dashboardSettings]
  );

  const handleSaveNotifications = useCallback(() => {
    clearGlobalError();
    
    // Convert to API-compatible format
    const apiPayload = {
      ...notificationSettings,
      digestFrequency: notificationSettings.digestFrequency as 'none' | 'daily' | 'weekly'
    };
    
    updateNotificationSettings(apiPayload);
  }, [updateNotificationSettings, notificationSettings, clearGlobalError]);

  const handleSaveDashboard = useCallback(() => {
    clearGlobalError();
    updateDashboardPreferences(dashboardSettings);
  }, [updateDashboardPreferences, dashboardSettings, clearGlobalError]);

  const handleSaveAll = useCallback(async () => {
    clearGlobalError();
    
    const promises = [];
    
    if (hasChanges) {
      // Convert notification settings to API-compatible format
      const apiNotificationPayload = {
        ...notificationSettings,
        digestFrequency: notificationSettings.digestFrequency as 'none' | 'daily' | 'weekly'
      };
      
      promises.push(updateNotificationSettings(apiNotificationPayload));
      promises.push(updateDashboardPreferences(dashboardSettings));
    }
    
    try {
      await Promise.all(promises);
      setSuccessMessage('All preferences updated successfully');
      setHasChanges(false);
    } catch (error) {
      // Individual error handling in mutation callbacks
    }
  }, [
    clearGlobalError,
    hasChanges,
    updateNotificationSettings,
    notificationSettings,
    updateDashboardPreferences,
    dashboardSettings
  ]);

  // ===== COMPUTED VALUES =====

  const isLoading = notificationLoading || dashboardLoading;
  const isSaving = updateNotificationLoading || updateDashboardLoading;
  const hasErrors = notificationError || dashboardError || updateNotificationError || updateDashboardError;

  const timezones = useMemo(() => [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney'
  ], []);

  // ===== RENDER =====

  if (isLoading) {
    return (
      <PageLayout title="Settings" subtitle="Manage your account preferences and notification settings">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    );
  }

  return (
    <SettingsErrorBoundary>
      <PageLayout 
        title="Settings" 
        subtitle="Manage your account preferences and notification settings"
      >
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 rounded-md p-4 flex items-center justify-between">
              <span>{successMessage}</span>
              <button 
                onClick={() => setSuccessMessage(null)}
                className="text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                aria-label="Dismiss success message"
              >
                ×
              </button>
            </div>
          )}

          {/* Error Display */}
          {hasErrors && (
            <ErrorAlert
              message="There was an error with your settings. Please try again."
              onClose={clearGlobalError}
            />
          )}

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8" role="tablist" aria-label="Application settings">
              {[
                { key: 'notifications', label: 'Notifications', icon: '🔔' },
                { key: 'dashboard', label: 'Dashboard', icon: '📊' },
                { key: 'display', label: 'Display', icon: '🎨' },
                { key: 'account', label: 'Account', icon: '👤' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  role="tab"
                  id={`tab-${tab.key}`}
                  aria-selected={activeTab === tab.key}
                  aria-controls={`tabpanel-${tab.key}`}
                  onClick={() => setActiveTab(tab.key as typeof activeTab)}
                  className={cn(
                    'flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm',
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  )}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'notifications' && (
              <div role="tabpanel" id="tabpanel-notifications" aria-labelledby="tab-notifications" className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">Notification Preferences</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Configure how you receive notifications</p>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* General Notification Settings */}
                  <div className="space-y-4">
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">General Settings</h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <label htmlFor="email-notifications" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Email Notifications
                          </label>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications via email</p>
                        </div>
                        <ToggleSwitch
                          id="email-notifications"
                          checked={notificationSettings.emailNotifications}
                          onChange={(checked) => handleNotificationChange('emailNotifications', checked)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label htmlFor="push-notifications" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Push Notifications
                          </label>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Receive browser push notifications</p>
                        </div>
                        <ToggleSwitch
                          id="push-notifications"
                          checked={notificationSettings.pushNotifications}
                          onChange={(checked) => handleNotificationChange('pushNotifications', checked)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label htmlFor="in-app-notifications" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            In-App Notifications
                          </label>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Show notifications within the application</p>
                        </div>
                        <ToggleSwitch
                          id="in-app-notifications"
                          checked={notificationSettings.inAppNotifications}
                          onChange={(checked) => handleNotificationChange('inAppNotifications', checked)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Notification Types */}
                  <div className="space-y-4">
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">Notification Types</h3>
                    
                    <div className="space-y-3">
                      {Object.entries(notificationSettings.notificationTypes).map(([type, enabled]) => (
                        <div key={type} className="flex items-center justify-between">
                          <div>
                            <label 
                              htmlFor={`notification-type-${type}`}
                              className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize"
                            >
                              {type.replace(/_/g, ' ').toLowerCase()}
                            </label>
                          </div>
                          <ToggleSwitch
                            id={`notification-type-${type}`}
                            checked={enabled}
                            onChange={(checked) => handleNotificationTypesChange(type as keyof NotificationPreferences['notificationTypes'], checked)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Quiet Hours */}
                  <div className="space-y-4">
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">Quiet Hours</h3>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <label htmlFor="quiet-hours-enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Enable Quiet Hours
                        </label>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Disable notifications during specific hours</p>
                      </div>
                      <ToggleSwitch
                        id="quiet-hours-enabled"
                        checked={notificationSettings.quietHours?.enabled || false}
                        onChange={(checked) => handleQuietHoursChange('enabled', checked)}
                      />
                    </div>

                    {notificationSettings.quietHours?.enabled && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ml-4">
                        <div>
                          <label htmlFor="quiet-start" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Start Time
                          </label>
                          <TimeInput
                            id="quiet-start"
                            value={notificationSettings.quietHours?.startTime || '22:00'}
                            onChange={(value) => handleQuietHoursChange('startTime', value)}
                          />
                        </div>
                        <div>
                          <label htmlFor="quiet-end" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            End Time
                          </label>
                          <TimeInput
                            id="quiet-end"
                            value={notificationSettings.quietHours?.endTime || '08:00'}
                            onChange={(value) => handleQuietHoursChange('endTime', value)}
                          />
                        </div>
                        <div>
                          <label htmlFor="quiet-timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Timezone
                          </label>
                          <select
                            id="quiet-timezone"
                            value={notificationSettings.quietHours?.timezone || 'UTC'}
                            onChange={(e) => handleQuietHoursChange('timezone', e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            {timezones.map(tz => (
                              <option key={tz} value={tz}>{tz}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Digest Frequency */}
                  <div className="space-y-4">
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">Digest Frequency</h3>
                    
                    <div>
                      <label htmlFor="digest-frequency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Digest Frequency
                      </label>
                      <select
                        id="digest-frequency"
                        value={notificationSettings.digestFrequency}
                        onChange={(e) => handleNotificationChange('digestFrequency', e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="none">None</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveNotifications}
                      disabled={isSaving}
                      variant="primary"
                    >
                      {updateNotificationLoading && <LoadingSpinner size="sm" />}
                      <span>Save Notification Preferences</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'dashboard' && (
              <div role="tabpanel" id="tabpanel-dashboard" aria-labelledby="tab-dashboard" className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">Dashboard Preferences</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Customize your dashboard experience</p>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* View Settings */}
                  <div className="space-y-4">
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">View Settings</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="default-view" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Default View
                        </label>
                        <select
                          id="default-view"
                          value={dashboardSettings.defaultView}
                          onChange={(e) => handleDashboardChange('defaultView', e.target.value)}
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="grid">Grid View</option>
                          <option value="list">List View</option>
                          <option value="cards">Card View</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="refresh-interval" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Refresh Interval (seconds)
                        </label>
                        <Input
                          id="refresh-interval"
                          type="number"
                          min="30"
                          max="3600"
                          value={dashboardSettings.dashboardRefreshInterval}
                          onChange={(e) => handleDashboardChange('dashboardRefreshInterval', parseInt(e.target.value))}
                          placeholder="300"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Display Options */}
                  <div className="space-y-4">
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">Display Options</h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <label htmlFor="show-completed" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Show Completed Actions
                          </label>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Display completed tasks and actions</p>
                        </div>
                        <ToggleSwitch
                          id="show-completed"
                          checked={dashboardSettings.showCompletedActions}
                          onChange={(checked) => handleDashboardChange('showCompletedActions', checked)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label htmlFor="action-filters" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Action Filters
                          </label>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Show filtering options for actions</p>
                        </div>
                        <ToggleSwitch
                          id="action-filters"
                          checked={dashboardSettings.actionFilters}
                          onChange={(checked) => handleDashboardChange('actionFilters', checked)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label htmlFor="auto-refresh" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Auto Refresh
                          </label>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Automatically refresh dashboard data</p>
                        </div>
                        <ToggleSwitch
                          id="auto-refresh"
                          checked={dashboardSettings.autoRefresh}
                          onChange={(checked) => handleDashboardChange('autoRefresh', checked)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label htmlFor="compact-mode" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Compact Mode
                          </label>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Use compact layout to show more information</p>
                        </div>
                        <ToggleSwitch
                          id="compact-mode"
                          checked={dashboardSettings.compactMode}
                          onChange={(checked) => handleDashboardChange('compactMode', checked)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Widget Configuration */}
                  <div className="space-y-4">
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">Dashboard Widgets</h3>
                    
                    <div className="space-y-3">
                      {dashboardSettings.widgetConfiguration && Object.entries(dashboardSettings.widgetConfiguration).map(([widget, enabled]) => (
                        <div key={widget} className="flex items-center justify-between">
                          <div>
                            <label 
                              htmlFor={`widget-${widget}`}
                              className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize"
                            >
                              {widget.replace(/([A-Z])/g, ' $1').replace(/^show/, 'Show')}
                            </label>
                          </div>
                          <ToggleSwitch
                            id={`widget-${widget}`}
                            checked={enabled}
                            onChange={(checked) => handleWidgetConfigChange(widget as keyof NonNullable<DashboardPreferences['widgetConfiguration']>, checked)}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveDashboard}
                      disabled={isSaving}
                      variant="primary"
                    >
                      {updateDashboardLoading && <LoadingSpinner size="sm" />}
                      <span>Save Dashboard Preferences</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'display' && (
              <div role="tabpanel" id="tabpanel-display" aria-labelledby="tab-display" className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">Display Preferences</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Customize your interface preferences</p>
                </div>
                
                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="theme" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Theme
                      </label>
                      <select
                        id="theme"
                        value={userSettings.theme}
                        onChange={(e) => handleUserChange('theme', e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                        <option value="system">System</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Language
                      </label>
                      <select
                        id="language"
                        value={userSettings.language}
                        onChange={(e) => handleUserChange('language', e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Timezone
                      </label>
                      <select
                        id="timezone"
                        value={userSettings.timezone}
                        onChange={(e) => handleUserChange('timezone', e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {timezones.map(tz => (
                          <option key={tz} value={tz}>{tz}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="time-format" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Time Format
                      </label>
                      <select
                        id="time-format"
                        value={userSettings.timeFormat}
                        onChange={(e) => handleUserChange('timeFormat', e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="12h">12 Hour (AM/PM)</option>
                        <option value="24h">24 Hour</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="date-format" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Date Format
                      </label>
                      <select
                        id="date-format"
                        value={userSettings.dateFormat}
                        onChange={(e) => handleUserChange('dateFormat', e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="yyyy-MM-dd">YYYY-MM-DD</option>
                        <option value="MM/dd/yyyy">MM/DD/YYYY</option>
                        <option value="dd/MM/yyyy">DD/MM/YYYY</option>
                        <option value="dd-MM-yyyy">DD-MM-YYYY</option>
                      </select>
                    </div>
                  </div>

                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                    Display preferences are applied immediately and stored locally in your browser.
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'account' && (
              <div role="tabpanel" id="tabpanel-account" aria-labelledby="tab-account" className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">Account Information</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Your account details and information</p>
                </div>
                
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Username
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{user?.username}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{user?.email}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Role
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white capitalize">{user?.role}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Organization
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {typeof user?.organization === 'string' 
                          ? user.organization 
                          : user?.organization?.name || 'Unknown'}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Department
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {user?.department || 'Not specified'}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Member Since
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {user?.createdAt ? formatDate(user.createdAt) : 'Unknown'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Save All Button */}
          {hasChanges && (
            <div className="flex justify-center">
              <Button
                onClick={handleSaveAll}
                disabled={isSaving}
                variant="primary"
                size="lg"
                className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
              >
                {isSaving && <LoadingSpinner size="sm" />}
                <span>Save All Changes</span>
              </Button>
            </div>
          )}
        </div>
      </PageLayout>
    </SettingsErrorBoundary>
  );
};